import { useEffect, useState } from "react"
import type { CSSProperties } from "react"

const STATUS_API_URL =
  (import.meta.env["VITE_STATUS_PAGE_API_URL"] as string | undefined) ??
  "https://status.entire.io/api/v1/summary"

const STATUS_PAGE_URL = STATUS_API_URL?.replace(/\/api\/v1\/summary$/, "")

interface StatusSummary {
  ongoing_incidents: unknown[]
  in_progress_maintenances: unknown[]
}

export function SystemStatus({
  className,
  pulse = false,
  style,
  textColorClassName,
  textSize = "text-sm",
}: {
  className?: string
  pulse?: boolean
  style?: CSSProperties
  textColorClassName?: string
  textSize?: string
}) {
  const [data, setData] = useState<StatusSummary | null>(null)
  const [isError, setIsError] = useState(false)

  useEffect(() => {
    let active = true
    async function poll() {
      try {
        const res = await fetch(STATUS_API_URL)
        if (!res.ok) throw new Error(`Status API ${res.status}`)
        const json = (await res.json()) as StatusSummary
        if (active) {
          setData(json)
          setIsError(false)
        }
      } catch {
        if (active) setIsError(true)
      }
    }
    poll()
    const id = setInterval(poll, 60_000)
    return () => {
      active = false
      clearInterval(id)
    }
  }, [])

  if (!data && !isError) return null

  const status =
    isError || !data
      ? "unknown"
      : (data.ongoing_incidents?.length ?? 0) > 0
        ? "degraded"
        : (data.in_progress_maintenances?.length ?? 0) > 0
          ? "maintenance"
          : "operational"

  const { dotColor, label, textColor } = {
    unknown: { dotColor: "bg-gray-400", label: "Status unknown", textColor: "text-gray-400" },
    degraded: {
      dotColor: "bg-amber-500",
      label: "Degraded",
      textColor: "text-muted hover:text-default",
    },
    maintenance: {
      dotColor: "bg-blue-500",
      label: "Maintenance",
      textColor: "text-muted hover:text-default",
    },
    operational: {
      dotColor: "bg-green-500",
      label: "Operational",
      textColor: "text-muted hover:text-default",
    },
  }[status]

  return (
    <a
      href={STATUS_PAGE_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`System status: ${label} (opens in new tab)`}
      className={`inline-flex items-center gap-2 ${textSize} ${textColorClassName ?? textColor} transition-colors ${className ?? ""}`}
      style={style}
    >
      <span
        className={`inline-block size-2 shrink-0 rounded-full ${dotColor} ${pulse ? "animate-pulse" : ""}`}
      />
      {label}
    </a>
  )
}
